import { usePinnedAutoScroll } from '@/hooks/usePinnedAutoScroll'
import { MessageBubble, TypingBubble } from './MessageBubble'
import { sumTurnUsage } from '../cost'
import type {
  AssistantDebugSelection,
  AssistantLiveStatus,
  ChatEntry,
  DisplayEntry,
  ToolCallResultEntry,
} from '../useAssistant'

type ConversationDetails = AssistantDebugSelection & {
  maxTokens?: number | null
  forcedCurrentDate?: string | null
  timezone?: string | null
  conversationKey?: string | null
}

type MessageListProps = {
  entries: ChatEntry[]
  streaming: boolean
  liveStatus?: AssistantLiveStatus
  onFollowUp: (text: string) => void
  // Opens the patient picker (with an optional mask hint); wired only to the thread's latest
  // assistant bubble.
  onSelectPatient?: (mask?: string) => void
  isFavorite?: (text: string) => boolean
  onToggleFavorite?: (text: string) => void
  conversationDetails?: ConversationDetails
  subAgentThread?: boolean
  threadPath?: string[] | null
  onOpenThread?: (threadPath: string[]) => void
}

export const MessageList = ({
  entries,
  streaming,
  liveStatus,
  onFollowUp,
  onSelectPatient,
  isFavorite,
  onToggleFavorite,
  conversationDetails,
  subAgentThread = false,
  threadPath,
  onOpenThread,
}: MessageListProps) => {
  // Follow new messages only while the user is pinned to the bottom of the chat.
  const { bottomRef, scrollContainerRef, onScroll } = usePinnedAutoScroll([entries, streaming])
  const debug = Boolean(conversationDetails)
  const flattened = flattenEntries(entries, debug)
  // Roll the per-turn usage up into a conversation total for the top debug bubble.
  // Summed from the visible turns (rather than the wire `conversation.usage`) so it
  // stays live as turns complete during streaming.
  const conversationUsage = debug ? sumTurnUsage(entries) : undefined
  const displayEntries: DisplayEntry[] = conversationDetails
    ? [
        {
          id: '__conversation-details',
          kind: 'hidden',
          ...conversationDetails,
          ...(conversationUsage && { usage: conversationUsage }),
        },
        ...flattened,
      ]
    : flattened
  // The live "thinking…" line comes from the in-flight turn's latest thinking
  // step (the projection keeps it in `processing` even in non-debug mode).
  const liveThinking = latestThinkingText(entries)
  // The "Select patient" action only belongs on an assistant bubble that is the
  // very last thing in the chat: the moment any message follows it (the user's
  // selection, a new turn) or a response starts streaming, it must disappear.
  const lastEntry = displayEntries[displayEntries.length - 1]
  const selectPatientEntryId =
    !streaming && lastEntry?.kind === 'assistant' ? lastEntry.id : undefined

  return (
    <div
      ref={scrollContainerRef}
      onScroll={onScroll}
      className="flex-1 overflow-y-auto bg-[#eef2f7] px-5 py-5"
    >
      <div className="flex w-full flex-col gap-4">
        {displayEntries.map((entry) => (
          <MessageBubble
            key={entry.id}
            entry={entry}
            onFollowUp={onFollowUp}
            onSelectPatient={entry.id === selectPatientEntryId ? onSelectPatient : undefined}
            isFavorite={isFavorite}
            onToggleFavorite={onToggleFavorite}
            showRawResponse={Boolean(conversationDetails)}
            subAgentThread={subAgentThread}
            threadPath={threadPath}
            onOpenThread={onOpenThread}
          />
        ))}
        {streaming && (
          <TypingBubble
            thinkingText={liveThinking}
            activityLabel={liveStatus?.activity}
            phaseLabel={liveStatus?.phase === 'composing' ? 'Composing answer…' : undefined}
          />
        )}
        <div ref={bottomRef} />
      </div>
    </div>
  )
}

type TurnEntry = Extract<ChatEntry, { kind: 'turn' }>

// Expand grouped wire entries into per-bubble render units. A `system` entry
// (debug only) becomes a system bubble; a `turn` becomes a user bubble, its
// processing steps (debug only), then an assistant bubble. tool_use/tool_result
// steps are paired into one tool_call_result bubble.
function flattenEntries(entries: ChatEntry[], debug: boolean): DisplayEntry[] {
  const units: DisplayEntry[] = []
  for (const entry of entries) {
    if (entry.kind === 'system') {
      if (debug) units.push({ id: entry.id, kind: 'system', text: entry.system.text })
      continue
    }
    if (entry.user) {
      units.push({ id: `${entry.id}:user`, kind: 'user', text: entry.user.text })
    }
    if (debug && entry.processing) {
      units.push(...flattenProcessing(entry.processing))
    }
    if (entry.assistant) {
      units.push({
        id: `${entry.id}:assistant`,
        kind: 'assistant',
        ...entry.assistant,
        ...(entry.usage && { usage: entry.usage }),
      })
    }
  }
  return units
}

function flattenProcessing(processing: NonNullable<TurnEntry['processing']>): DisplayEntry[] {
  const resultByUseId = new Map<
    string,
    {
      id: string
      content?: string
      isError?: boolean
      artifacts?: ToolCallResultEntry['artifacts']
    }
  >()
  for (const step of processing) {
    if (step.type === 'tool_result') {
      resultByUseId.set(step.toolUseId, {
        id: step.id,
        // `result` is the structured ToolResponse; serialize it for the
        // debug-view output string, and carry its artifacts through so the
        // tool bubble can render them like the assistant does.
        content: JSON.stringify(step.result),
        ...(step.isError !== undefined && { isError: step.isError }),
        ...(step.result.artifacts !== undefined && { artifacts: step.result.artifacts }),
      })
    }
  }

  const units: DisplayEntry[] = []
  for (const step of processing) {
    if (step.type === 'thinking') {
      units.push({ id: step.id, kind: 'thinking', text: step.text })
    } else if (step.type === 'retry') {
      // No wire `id` on a retry step (unlike other processing steps) — its
      // `createdAt` is unique enough to key on, since each retry is a full
      // model round trip apart from the last.
      units.push({ id: `retry:${step.createdAt}`, kind: 'retry', assistant: step.assistant })
    } else if (step.type === 'tool_use') {
      const result = resultByUseId.get(step.toolUseId)
      units.push({
        id: `${step.id}:${result?.id ?? 'pending'}`,
        kind: 'tool_call_result',
        toolUseId: step.toolUseId,
        name: step.name,
        input: step.input,
        ...(result?.content !== undefined && { output: result.content }),
        ...(result?.isError !== undefined && { isError: result.isError }),
        ...(result?.artifacts !== undefined && { artifacts: result.artifacts }),
      })
    }
    // tool_result steps are surfaced via the paired tool_use above.
  }
  return units
}

function latestThinkingText(entries: ChatEntry[]): string | undefined {
  const last = entries[entries.length - 1]
  if (last?.kind !== 'turn' || !last.processing) return undefined
  for (let i = last.processing.length - 1; i >= 0; i--) {
    const step = last.processing[i]
    if (step.type === 'thinking') return step.text
  }
  return undefined
}
